import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, Target, Shield, TrendingUp } from 'lucide-react';
import { EnhancedContentAnalyzer } from '@/components/ai/EnhancedContentAnalyzer';
import { SmartRecommendationsPanel } from '@/components/ai/SmartRecommendationsPanel';
import NuancedFilterTester from '@/components/ai/NuancedFilterTester';

export default function AILab() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            AI Laboratory
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Advanced AI-powered tools for content analysis and smart recommendations
          </p>
        </div>

        <Tabs defaultValue="analyzer" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="analyzer" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Enhanced Analysis
            </TabsTrigger>
            <TabsTrigger value="nuanced" className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              Nuanced Filter
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Recommendations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analyzer">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-6 w-6 text-primary" />
                  AI Content Analysis Engine
                </CardTitle>
                <CardDescription>
                  Powered by Claude Opus 4, GPT-4, and Gemini for comprehensive content moderation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="bg-green-100 dark:bg-green-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="font-semibold">Multi-Model Analysis</h3>
                    <p className="text-sm text-muted-foreground">
                      Uses multiple AI models for accurate content assessment
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <Brain className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="font-semibold">Deep Insights</h3>
                    <p className="text-sm text-muted-foreground">
                      Sentiment, emotions, entities, and risk scoring
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="font-semibold">Real-time Processing</h3>
                    <p className="text-sm text-muted-foreground">
                      Instant analysis with confidence scoring
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <EnhancedContentAnalyzer />
          </TabsContent>

          <TabsContent value="nuanced">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-6 w-6 text-primary" />
                  Nuanced Content Filtering
                </CardTitle>
                <CardDescription>
                  Advanced filtering with context-aware rule weighting, multi-factor decisions, and intent analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="bg-indigo-100 dark:bg-indigo-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <Brain className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h3 className="font-semibold">Context-Aware Weighting</h3>
                    <p className="text-sm text-muted-foreground">
                      Rules weighted by platform, time, and user behavior patterns
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-emerald-100 dark:bg-emerald-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <Target className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="font-semibold">Multi-Factor Decisions</h3>
                    <p className="text-sm text-muted-foreground">
                      Combines rule matching, AI analysis, and intent detection
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-violet-100 dark:bg-violet-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <Shield className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                    </div>
                    <h3 className="font-semibold">Intent Analysis</h3>
                    <p className="text-sm text-muted-foreground">
                      Understands content purpose: promotional, manipulative, social
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <NuancedFilterTester />
          </TabsContent>

          <TabsContent value="recommendations">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-6 w-6 text-primary" />
                  Smart Recommendation System
                </CardTitle>
                <CardDescription>
                  AI-driven content recommendations based on user behavior and preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="bg-orange-100 dark:bg-orange-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <Brain className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <h3 className="font-semibold">Behavioral Learning</h3>
                    <p className="text-sm text-muted-foreground">
                      Learns from your interactions to improve recommendations
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-pink-100 dark:bg-pink-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <Target className="h-6 w-6 text-pink-600 dark:text-pink-400" />
                    </div>
                    <h3 className="font-semibold">Personalized</h3>
                    <p className="text-sm text-muted-foreground">
                      Tailored to your interests, topics, and preferred creators
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-cyan-100 dark:bg-cyan-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <TrendingUp className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <h3 className="font-semibold">Performance Tracking</h3>
                    <p className="text-sm text-muted-foreground">
                      Analytics on recommendation effectiveness
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <SmartRecommendationsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}